/**
 * Leviathan Web - Today's Dispatches Widget
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Shows count of today's dispatches + upcoming list
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Truck } from 'lucide-react';
import { API_URL } from '@/config/api';

interface DispatchSummary {
  id: string;
  dispatchNumber: string;
  status: string;
  scheduledDate: string;
  order: { customer: { name: string } } | null;
  driver: { firstName: string; lastName: string } | null;
}

const TodaysDispatchesWidget = () => {
  const { accessToken } = useAuth();
  const [count, setCount] = useState(0);
  const [dispatches, setDispatches] = useState<DispatchSummary[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/analytics/dashboard`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          // Count and list must describe the same set. The list used to come
          // from recentActivity.dispatches — the five most recent dispatches
          // ever, with no date filter — so the widget could show "0" above a
          // populated list.
          setCount(data.dashboard?.kpis?.dispatchesToday ?? 0);
          setDispatches(data.dashboard?.todaysDispatches?.slice(0, 5) ?? []);
        }
      } catch {
        // silent
      }
    };
    fetchData();
  }, [accessToken]);

  const statusColors: Record<string, string> = {
    SCHEDULED: 'text-amber bg-amber/10',
    EN_ROUTE: 'text-warning-amber bg-warning-amber/10',
    ARRIVED: 'text-steel bg-steel/10',
    DELIVERING: 'text-warning-amber bg-warning-amber/10',
    COMPLETED: 'text-success-green bg-success-green/10',
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center space-x-2 mb-3">
        <Truck className="w-4 h-4 text-amber" />
        <span className="text-[10px] uppercase tracking-[0.15em] text-text-secondary font-mono">
          Today's Dispatches
        </span>
      </div>

      <div
        className="text-3xl font-mono font-bold text-amber mb-3"
        style={{ textShadow: '0 0 15px rgba(255,149,0,0.6)' }}
      >
        {count}
      </div>

      <div className="space-y-1 mt-auto overflow-y-auto">
        {dispatches.map((d) => (
          <Link
            key={d.id}
            to={`/dispatch/${d.id}`}
            className="flex items-center justify-between py-1.5 px-2 -mx-1 rounded hover:bg-panel-border/20 transition-colors"
          >
            <div className="min-w-0 flex-1">
              <div className="text-xs font-mono text-text-primary truncate">{d.dispatchNumber}</div>
              <div className="text-[10px] text-text-tertiary truncate">
                {d.driver ? `${d.driver.firstName} ${d.driver.lastName}` : 'Unassigned'}
              </div>
            </div>
            <span
              className={`ml-2 px-1.5 py-0.5 text-[9px] font-mono uppercase rounded ${statusColors[d.status] || 'text-text-secondary bg-text-secondary/10'}`}
            >
              {d.status.replace('_', ' ')}
            </span>
          </Link>
        ))}
        {dispatches.length === 0 && (
          <div className="text-xs text-text-tertiary font-mono text-center py-2">
            No dispatches today
          </div>
        )}
      </div>
    </div>
  );
};

export default TodaysDispatchesWidget;
